/**
 * City code → IANA timezone (API-1701), derived from the shared city record
 * (API-1704).
 *
 * The ONE place a LOCI city code is translated into the timezone whose wall
 * clock its surfaces should read. Every surface that turns a stored instant
 * into a *local hour* — late-night predicates, attendance heatmaps, "tonight"
 * windows — must resolve its zone here rather than inlining
 * `'America/New_York'`.
 *
 * Why a function and not a literal: two neighborhood surfaces
 * (`/neighborhoods/:slug/categories`, `/neighborhoods/:slug/density`) shipped
 * the MVP market's zone as a hardcoded string. That is invisible-wrong the day
 * a second city launches — a Chicago neighborhood's "late night" bar would be
 * computed against Eastern time and nothing would fail, it would just be off by
 * an hour.
 *
 * **The zones themselves are not defined here.** API-1701 declared this map the
 * one registry and then API-1692 immediately added a second one next door
 * (`VENUE_CITY_TIMEZONE_MAP`), with a third already live in the web app — which
 * is what API-1704 collapsed. `CITY_TIME_ZONE_MAP` is now a *projection* of
 * `CITY_RECORDS` in `@loci/city-editions`, so a city cannot have a zone here and
 * a different one on the home feed; there is only one place to write a zone.
 *
 * **Exhaustiveness is still the type system, not a test.** The projection is
 * built from every key of the registry and typed `Record<CityCode, string>`,
 * where `CityCode` *is* `keyof typeof CITY_RECORDS` — so a city cannot exist
 * without a zone, by construction rather than by lockstep.
 */
import { CITY_RECORDS, type CityCode } from '@loci/city-editions';

/**
 * Zone used for a code that is not a registered {@link CityCode} at all.
 *
 * The `?city=` query param is validated only as three uppercase letters, so an
 * unregistered code can reach these surfaces; Eastern is the MVP market's zone,
 * so it degrades to today's behaviour rather than throwing a 500 out of a
 * public read path. This is NOT the escape hatch for a new city — a new city
 * belongs in `CITY_RECORDS`, and the type system enforces that.
 */
export const DEFAULT_CITY_TIME_ZONE = 'America/New_York';

/**
 * Public city code → IANA timezone identifier. Codes are the 3-letter
 * uppercase form the `?city=` query param carries (e.g. `CLT`).
 *
 * A derived view of {@link CITY_RECORDS} (API-1704) — never edited directly.
 * To give a city a zone, or to add a city, edit the record in
 * `packages/city-editions/src/cities.ts`; this map and every other per-city
 * surface follow from it.
 */
export const CITY_TIME_ZONE_MAP: Readonly<Record<CityCode, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(CITY_RECORDS).map(([code, record]) => [code, record.timeZone]),
  ) as Record<CityCode, string>,
);

/** True when `cityCode` has an explicit timezone entry. */
export const isKnownCityTimeZone = (cityCode: string): boolean =>
  Object.prototype.hasOwnProperty.call(CITY_TIME_ZONE_MAP, cityCode.trim().toUpperCase());

/**
 * Resolve a city code to its IANA timezone, falling back to
 * {@link DEFAULT_CITY_TIME_ZONE} for a code outside the registry.
 *
 * Case- and whitespace-insensitive: callers whose city param has already been
 * normalised by `neighborhoodsCityQuerySchema` (trim + uppercase) pass through
 * unchanged, and callers that haven't normalised still resolve correctly.
 *
 * @param cityCode public city code, e.g. `'CLT'`
 * @returns an IANA timezone identifier, never empty
 */
export function cityTimeZone(cityCode: string | null | undefined): string {
  if (typeof cityCode !== 'string') {
    return DEFAULT_CITY_TIME_ZONE;
  }
  const normalized = cityCode.trim().toUpperCase();
  const zones: Record<string, string | undefined> = CITY_TIME_ZONE_MAP;
  return zones[normalized] ?? DEFAULT_CITY_TIME_ZONE;
}
